import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Pencil, Trash2, Search, Rocket, Undo2, History, ArrowUpDown, ArrowUp, ArrowDown, Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import {
  fetchDeviceStockPaged,
  createDeviceStock,
  updateDeviceStock,
  deployDeviceStock,
  returnDeviceStock,
  fetchCustomers,
  fetchDeviceStockHistory,
  fetchSimCards,
  createSimCard,
  fetchStockLocations,
  createStockLocation,
  deleteStockLocation,
  DeviceStock,
} from "@/lib/reseller-api";
import { fetchDevices, fetchPermissionsMap, fetchUsers, userDevicesFromMap, updateDevice, type Device } from "@/lib/traccar-api";
import { AddVehicleDialog } from "@/components/devices/AddVehicleDialog";
import { CustomerDialog } from "@/components/customers/CustomerDialog";

const MANUFACTURER_MODELS: Record<string, string[]> = {
  AOVX: ["VL100", "VL300"],
  Coban: ["BN303"],
  Concox: ["GT06N", "JM-BL10", "JM-VL01", "JM-VL02", "JM-VL03", "JM-VL04", "JM-VL05", "WeTrack2"],
  Globalstar: ["AtlasTrax"],
  JMAK: ["JR12"],
  Mobilogix: ["MT2000"],
  "Jimi IoT": ["JC371", "JC400P"],
  Ruptela: ["FM-Eco4", "FM-Eco4+", "FM-Pro4", "HCV5"],
  Suntech: ["ST300", "ST310", "ST340", "ST600"],
  Teltonika: ["FMB003", "FMC150", "FMT100"],
  X3Tech: ["NT20", "NT40"],
  Kingwo: ["LT32D", "LT100"],
  BSJ: ["EG06G"]
};

const MANUFACTURERS = Object.keys(MANUFACTURER_MODELS).sort((a, b) =>
  a.localeCompare(b, undefined, { sensitivity: "base" })
);

const statusColors: Record<DeviceStock["status"], string> = {
  in_stock: "bg-green-500",
  deployed: "bg-blue-500",
  returned: "bg-yellow-500",
  maintenance: "bg-orange-500",
  retired: "bg-gray-500",
  deleted: "bg-red-800",
};

const normalizeImei = (value: string | null | undefined) => value?.trim() ?? "";

export default function DeviceStockPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState(() => searchParams.get("imei") ?? "");
  const [search, setSearch] = useState(() => searchParams.get("imei") ?? "");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [isOpen, setIsOpen] = useState(false);
  const [isDeployOpen, setIsDeployOpen] = useState(false);
  const [isReturnOpen, setIsReturnOpen] = useState(false);
  const [isReturning, setIsReturning] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [editingDevice, setEditingDevice] = useState<DeviceStock | null>(null);
  const [selectedDevice, setSelectedDevice] = useState<DeviceStock | null>(null);
  const [historyDevice, setHistoryDevice] = useState<DeviceStock | null>(null);
  const [deployCustomerId, setDeployCustomerId] = useState<string>("");
  const [deployTraccarDeviceId, setDeployTraccarDeviceId] = useState<string>("");
  const [deployCustomerOpen, setDeployCustomerOpen] = useState(false);
  const [deployVehicleOpen, setDeployVehicleOpen] = useState(false);
  const [deployVehicleSearch, setDeployVehicleSearch] = useState("");
  const [overwriteWarningVehicle, setOverwriteWarningVehicle] = useState<Device | null>(null);
  const [isAddVehicleFromDeploy, setIsAddVehicleFromDeploy] = useState(false);
  const [isAddCustomerFromDeploy, setIsAddCustomerFromDeploy] = useState(false);
  const [isDeploying, setIsDeploying] = useState(false);
  const [selectedManufacturer, setSelectedManufacturer] = useState<string>("");
  const [selectedModel, setSelectedModel] = useState<string>("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState<string>("created_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [simOpen, setSimOpen] = useState(false);
  const [selectedSimId, setSelectedSimId] = useState<string>("none");
  const [createNewSim, setCreateNewSim] = useState(false);
  const [newSimIccid, setNewSimIccid] = useState("");
  const [newSimPhone, setNewSimPhone] = useState("");
  const [locationOpen, setLocationOpen] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState<string>("none");
  const [createNewLocation, setCreateNewLocation] = useState(false);
  const [newLocationName, setNewLocationName] = useState("");
  const [savingLocation, setSavingLocation] = useState(false);
  const [isManageLocationsOpen, setIsManageLocationsOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const PAGE_SIZE = 50;
  const [page, setPage] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data: customers = [] } = useQuery({
    queryKey: ["customers"],
    queryFn: fetchCustomers,
  });

  const { data: traccarDevices = [] } = useQuery({
    queryKey: ["devices"],
    queryFn: fetchDevices,
    staleTime: 60000,
  });

  const { data: traccarUsers = [] } = useQuery({
    queryKey: ["users"],
    queryFn: fetchUsers,
  });

  const traccarDeviceByUniqueId = useMemo(() => {
    const map = new Map<string, (typeof traccarDevices)[0]>();
    traccarDevices.forEach(d => {
      const uniqueId = normalizeImei(d.uniqueId);
      if (uniqueId) map.set(uniqueId, d);
    });
    return map;
  }, [traccarDevices]);

  const { data: permsMap } = useQuery({
    queryKey: ["permissions-map"],
    queryFn: fetchPermissionsMap,
    enabled: customers.some((customer) => Boolean(customer.traccar_user_id)) && traccarDevices.length > 0,
  });

  const customerByTraccarDeviceId = useMemo(() => {
    if (!permsMap) return new Map<number, (typeof customers)[number]>();

    const customerByUserId = new Map(
      customers
        .filter((customer) => customer.traccar_user_id)
        .map((customer) => [customer.traccar_user_id!, customer] as const)
    );
    const userOrder = new Map(traccarUsers.map((user, index) => [user.id, index]));
    const directCustomers = new Map<number, typeof customers>();
    const groupCustomers = new Map<number, typeof customers>();

    // Preserve Traccar's user order so conflicts resolve exactly as on /devices.
    for (const user of traccarUsers) {
      const customer = customerByUserId.get(user.id);
      if (!customer) continue;
      for (const deviceId of permsMap.devicesByUser[user.id] ?? []) {
        const matches = directCustomers.get(deviceId) ?? [];
        matches.push(customer);
        directCustomers.set(deviceId, matches);
      }
      for (const groupId of permsMap.groupsByUser[user.id] ?? []) {
        const matches = groupCustomers.get(groupId) ?? [];
        matches.push(customer);
        groupCustomers.set(groupId, matches);
      }
    }

    const resolved = new Map<number, (typeof customers)[number]>();
    for (const device of traccarDevices) {
      const candidates = [
        ...(directCustomers.get(device.id) ?? []),
        ...(groupCustomers.get(device.groupId) ?? []),
      ];
      const unique = Array.from(new Map(candidates.map((customer) => [customer.id, customer])).values());
      unique.sort((a, b) =>
        (userOrder.get(a.traccar_user_id!) ?? Number.MAX_SAFE_INTEGER)
        - (userOrder.get(b.traccar_user_id!) ?? Number.MAX_SAFE_INTEGER)
      );
      if (unique[0]) resolved.set(device.id, unique[0]);
    }
    return resolved;
  }, [customers, permsMap, traccarDevices, traccarUsers]);

  // Match the same way the table resolves vehicle and customer (IMEI -> Traccar
  // device -> owning customer), since device_stock.customer_id / traccar_device_id
  // are often missing or stale for deployed devices.
  const matchingTraccar = useMemo(() => {
    if (!search) return undefined;
    const lower = search.toLowerCase();
    const matched = traccarDevices.filter(d =>
      d.name.toLowerCase().includes(lower)
      || customerByTraccarDeviceId.get(d.id)?.name.toLowerCase().includes(lower)
    );
    if (matched.length === 0) return undefined;
    return {
      ids: matched.map(d => d.id),
      imeis: matched.map(d => normalizeImei(d.uniqueId)).filter(Boolean),
    };
  }, [search, traccarDevices, customerByTraccarDeviceId]);

  const { data: pagedResult, isLoading } = useQuery({
    queryKey: ["device-stock", "paged", page, search, statusFilter, sortBy, sortDir, matchingTraccar],
    queryFn: () => fetchDeviceStockPaged({
      page, limit: PAGE_SIZE, search, status: statusFilter, sortBy, sortDir,
      traccarDeviceIds: matchingTraccar?.ids,
      imeis: matchingTraccar?.imeis,
    }),
  });
  const devices = useMemo(() => pagedResult?.data ?? [], [pagedResult?.data]);
  const total = pagedResult?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const autoFixedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (isLoading || traccarDevices.length === 0) return;
    const toFix = devices.filter(
      (d) =>
        d.status === "deployed" &&
        !d.traccar_device_id &&
        d.imei &&
        !traccarDeviceByUniqueId.has(normalizeImei(d.imei)) &&
        !autoFixedRef.current.has(d.id)
    );
    if (toFix.length === 0) return;
    toFix.forEach((d) => autoFixedRef.current.add(d.id));
    Promise.all(toFix.map((d) => updateDeviceStock(d.id, { status: "in_stock" }))).then(() => {
      queryClient.invalidateQueries({ queryKey: ["device-stock"] });
    });
  }, [devices, traccarDeviceByUniqueId, isLoading, traccarDevices.length, queryClient]);

  const { data: simCards = [] } = useQuery({
    queryKey: ["sim-cards"],
    queryFn: fetchSimCards,
  });

  const { data: stockLocations = [] } = useQuery({
    queryKey: ["stock-locations"],
    queryFn: fetchStockLocations,
  });

  // Get the selected customer's traccar_user_id
  const selectedCustomer = customers.find((c) => c.id === deployCustomerId);

  const customerVehicles = useMemo(
    () => (selectedCustomer?.traccar_user_id && permsMap)
      ? userDevicesFromMap(selectedCustomer.traccar_user_id, traccarDevices, permsMap)
      : [],
    [selectedCustomer?.traccar_user_id, permsMap, traccarDevices]
  );

  const { data: history = [], isLoading: historyLoading } = useQuery({
    queryKey: ["device-stock-history", historyDevice?.id],
    queryFn: () => fetchDeviceStockHistory(historyDevice!.id),
    enabled: !!historyDevice,
  });

  const createMutation = useMutation({
    mutationFn: createDeviceStock,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["device-stock"] });
      setIsOpen(false);
      toast({ title: t("reseller.deviceStock.created") });
    },
    onError: (error: Error) => {
      console.error("[device-stock] create:", error);
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<DeviceStock> }) => {
      console.log("[device-stock] update request", {
        id,
        model: data.model,
        status: data.status,
      });
      return updateDeviceStock(id, data);
    },
    onSuccess: (updatedDevice) => {
      console.log("[device-stock] update success", {
        id: updatedDevice.id,
        model: updatedDevice.model,
        traccarDeviceId: updatedDevice.traccar_device_id,
        installedOnVehicle: Boolean(updatedDevice.traccar_device_id),
      });
      queryClient.invalidateQueries({ queryKey: ["device-stock"] });
      queryClient.invalidateQueries({ queryKey: ["devices"] });
      setIsOpen(false);
      setEditingDevice(null);
      toast({ title: t("reseller.deviceStock.updated") });
    },
    onError: (error: Error) => {
      console.error("[device-stock] update:", error);
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDeleteProgress, setBulkDeleteProgress] = useState<{ done: number; total: number } | null>(null);

  useEffect(() => {
    if (!bulkDeleting) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [bulkDeleting]);

  const handleBulkDelete = async () => {
    const ids = [...selectedIds];
    if (!confirm(t("reseller.deviceStock.bulkDeleteConfirm", { count: ids.length }))) return;
    setBulkDeleting(true);
    setBulkDeleteProgress({ done: 0, total: ids.length });
    try {
      for (let i = 0; i < ids.length; i++) {
        await updateDeviceStock(ids[i], { status: "deleted" });
        setBulkDeleteProgress({ done: i + 1, total: ids.length });
      }
      queryClient.invalidateQueries({ queryKey: ["device-stock"] });
      setSelectedIds(new Set());
      toast({ title: t("reseller.deviceStock.bulkDeleted", { count: ids.length }) });
    } catch (err) {
      toast({ title: t("common.error"), description: (err as Error).message, variant: "destructive" });
    } finally {
      setBulkDeleting(false);
      setBulkDeleteProgress(null);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === devices.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(devices.map((d) => d.id)));
    }
  };

  const deployMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { traccar_device_id: number; customer_id: string; vehicle_id?: string } }) =>
      deployDeviceStock(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["device-stock"] });
      setIsDeployOpen(false);
      setSelectedDevice(null);
      setIsDeploying(false);
      toast({ title: t("reseller.deviceStock.deployed") });
    },
    onError: (error: Error) => {
      setIsDeploying(false);
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const returnMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data?: { return_reason?: string } }) => returnDeviceStock(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ["device-stock"]}).then();
      setIsReturnOpen(false);
      setSelectedDevice(null);
      toast({ title: t("reseller.deviceStock.returned") });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });


  const deleteLocationMutation = useMutation({
    mutationFn: deleteStockLocation,
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["stock-locations"] });
      if (selectedLocationId === id) setSelectedLocationId("none");
      toast({ title: t("reseller.deviceStock.locationDeleted") });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const handleSaveNewLocation = async () => {
    if (!newLocationName.trim()) {
      toast({ title: t("common.error"), description: t("reseller.deviceStock.locationNameRequired") || "Location name is required", variant: "destructive" });
      return;
    }
    setSavingLocation(true);
    try {
      const newLoc = await createStockLocation({ name: newLocationName.trim() });
      queryClient.invalidateQueries({ queryKey: ["stock-locations"] });
      setSelectedLocationId(newLoc.id);
      setCreateNewLocation(false);
      setNewLocationName("");
    } catch (err) {
      toast({ title: t("common.error"), description: (err as Error).message, variant: "destructive" });
    } finally {
      setSavingLocation(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (!selectedManufacturer) {
      toast({ title: t("common.error"), description: t("reseller.deviceStock.manufacturerRequired"), variant: "destructive" });
      return;
    }
    if (!selectedModel) {
      toast({ title: t("common.error"), description: t("reseller.deviceStock.modelRequired"), variant: "destructive" });
      return;
    }
    if (createNewSim && !newSimIccid.trim()) {
      toast({ title: t("common.error"), description: "ICCID is required for new SIM card", variant: "destructive" });
      return;
    }
    if (createNewLocation && !newLocationName.trim()) {
      toast({ title: t("common.error"), description: t("reseller.deviceStock.locationNameRequired") || "Location name is required", variant: "destructive" });
      return;
    }

    let simCardId: string | null = selectedSimId === "none" ? null : selectedSimId || null;
    let locationId: string | null = selectedLocationId === "none" ? null : selectedLocationId || null;

    if (createNewSim && newSimIccid.trim()) {
      try {
        const newSim = await createSimCard({ iccid: newSimIccid.trim(), ...(newSimPhone.trim() && { phone_number: newSimPhone.trim() }), status: "active" });
        simCardId = newSim.id;
        queryClient.invalidateQueries({ queryKey: ["sim-cards"] });
      } catch (err) {
        toast({ title: t("common.error"), description: (err as Error).message, variant: "destructive" });
        return;
      }
    }

    if (createNewLocation && newLocationName.trim()) {
      try {
        const newLoc = await createStockLocation({ name: newLocationName.trim() });
        locationId = newLoc.id;
        queryClient.invalidateQueries({ queryKey: ["stock-locations"] });
      } catch (err) {
        toast({ title: t("common.error"), description: (err as Error).message, variant: "destructive" });
        return;
      }
    }

    const formData = new FormData(form);
    const data = {
      serial_number: (formData.get("serial_number") as string) || null,
      imei: (formData.get("imei") as string) || null,
      model: selectedModel,
      manufacturer: selectedManufacturer,
      sim_card_id: simCardId,
      location_id: locationId,
      supplier: (formData.get("supplier") as string) || null,
      purchase_date: (formData.get("purchase_date") as string) || null,
      purchase_price: formData.get("purchase_price") ? parseFloat(formData.get("purchase_price") as string) : null,
      warranty_expiry: (formData.get("warranty_expiry") as string) || null,
      status: formData.get("status") as DeviceStock["status"],
      notes: (formData.get("notes") as string) || null,
    };

    if (editingDevice) {
      console.log("[device-stock] edit submitted", {
        id: editingDevice.id,
        previousModel: editingDevice.model,
        nextModel: data.model,
        traccarDeviceId: editingDevice.traccar_device_id,
        installedOnVehicle: Boolean(editingDevice.traccar_device_id),
      });
      updateMutation.mutate({ id: editingDevice.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const performDeploy = async (vehicle: Device, deviceToDeploy: DeviceStock, customerId: string) => {
    setIsDeploying(true);
    try {
      const updates: typeof vehicle = { ...vehicle };
      updates.uniqueId = deviceToDeploy.imei;
      updates.phone = deviceToDeploy.sim_card?.phone_number || deviceToDeploy.sim_card?.iccid;
      updates.model = deviceToDeploy.model;
      await updateDevice(updates);
      await queryClient.invalidateQueries({ queryKey: ["devices"] });
      deployMutation.mutate({ id: deviceToDeploy.id, data: { traccar_device_id: vehicle.id, customer_id: customerId } });
    } catch (err) {
      setIsDeploying(false);
      toast({ title: t("common.error"), description: (err as Error).message, variant: "destructive" });
    }
  };

  const handleDeploySubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedDevice || !deployCustomerId || !deployTraccarDeviceId) return;
    const traccarDeviceId = parseInt(deployTraccarDeviceId);
    const vehicle = customerVehicles.find((v) => v.id === traccarDeviceId);
    if (!vehicle) return;
    const hasExistingDevice = Boolean(vehicle.uniqueId) && !vehicle.uniqueId.startsWith("not_installed_");
    if (hasExistingDevice) {
      setOverwriteWarningVehicle(vehicle);
      return;
    }
    performDeploy(vehicle, selectedDevice, deployCustomerId);
  };

  const resetDeployState = () => {
    setSelectedDevice(null);
    setDeployCustomerId("");
    setDeployTraccarDeviceId("");
    setDeployCustomerOpen(false);
    setDeployVehicleOpen(false);
    setDeployVehicleSearch("");
    setIsAddVehicleFromDeploy(false);
    setIsAddCustomerFromDeploy(false);
    setIsDeploying(false);
    setOverwriteWarningVehicle(null);
  };

  const handleReturnSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedDevice) return;
    const formData = new FormData(e.currentTarget);
    const data = {
      return_reason: (formData.get("return_reason") as string) || undefined,
    };
    setIsReturning(true);
    try {
      if (selectedDevice.traccar_device_id) {
        const allDevices = await fetchDevices();
        const vehicle = allDevices.find((d) => d.id === selectedDevice.traccar_device_id);
        if (vehicle) {
          const updates = { ...vehicle, uniqueId: `not_installed_${crypto.randomUUID()}`, phone: "", model: "" };
          await updateDevice(updates).catch(console.error);
          await queryClient.invalidateQueries({ queryKey: ["devices"] });
        }
      }
      returnMutation.mutate({ id: selectedDevice.id, data });
    } finally {
      setIsReturning(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {bulkDeleting && (
          <div className="bg-destructive/10 border border-destructive rounded-lg px-4 py-3 flex items-center gap-3">
            <span className="text-lg font-semibold text-destructive animate-pulse">{t("common.doNotLeave")}</span>
            <span className="text-sm text-destructive">{bulkDeleteProgress ? `${bulkDeleteProgress.done} / ${bulkDeleteProgress.total}` : ""}</span>
          </div>
        )}
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">
            {t("reseller.deviceStock.title")}
            {!isLoading && <span className="text-muted-foreground text-xl font-normal ml-2">{t("reseller.deviceStock.count", { count: total })}</span>}
          </h1>
          <Dialog open={isOpen} onOpenChange={(open) => { setIsOpen(open); if (!open) { setEditingDevice(null); setSelectedManufacturer(""); setSelectedModel(""); setSelectedSimId("none"); setCreateNewSim(false); setNewSimIccid(""); setNewSimPhone(""); setSelectedLocationId("none"); setCreateNewLocation(false); setNewLocationName(""); } }}>
            <DialogTrigger asChild>
              <Button><Plus className="h-4 w-4 mr-2" /> {t("reseller.deviceStock.add")}</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingDevice ? t("reseller.deviceStock.edit") : t("reseller.deviceStock.add")}</DialogTitle>
                <DialogDescription className="sr-only">
                  {editingDevice ? "Update this stock device record." : "Create a stock device record."}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="imei">ID *</Label>
                    <Input id="imei" name="imei" required defaultValue={editingDevice?.imei || ""} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="serial_number">{t("reseller.deviceStock.serialNumber")}</Label>
                    <Input id="serial_number" name="serial_number" defaultValue={editingDevice?.serial_number || ""} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t("reseller.deviceStock.manufacturer")} *</Label>
                    <Select
                      name="manufacturer"
                      required
                      value={selectedManufacturer}
                      onValueChange={(v) => { setSelectedManufacturer(v); setSelectedModel(""); }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t("reseller.deviceStock.manufacturer")} />
                      </SelectTrigger>
                      <SelectContent>
                        {MANUFACTURERS.map((m) => (
                          <SelectItem key={m} value={m}>{m}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t("reseller.deviceStock.model")} *</Label>
                    <Select
                      name="model"
                      required
                      value={selectedModel}
                      onValueChange={setSelectedModel}
                      disabled={!selectedManufacturer}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t("reseller.deviceStock.model")} />
                      </SelectTrigger>
                      <SelectContent>
                        {(MANUFACTURER_MODELS[selectedManufacturer] ?? []).map((m) => (
                          <SelectItem key={m} value={m}>{m}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t("reseller.deviceStock.simCard")}</Label>
                    {createNewSim ? (
                      <Button type="button" variant="outline" className="w-full justify-between font-normal" onClick={() => { setCreateNewSim(false); setNewSimIccid(""); setNewSimPhone(""); setSelectedSimId("none"); }}>
                        <span className="truncate">{t("reseller.deviceStock.createNewSim") || "Create new SIM"}</span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    ) : (
                      <Popover open={simOpen} onOpenChange={setSimOpen}>
                        <PopoverTrigger asChild>
                          <Button variant="outline" role="combobox" aria-expanded={simOpen} className="w-full justify-between font-normal">
                            <span className="truncate">
                              {selectedSimId === "none"
                                ? t("reseller.deviceStock.noSimCard")
                                : (() => { const s = simCards.find((x) => x.id === selectedSimId); return s ? `${s.iccid}${s.phone_number ? ` (${s.phone_number})` : ""}` : t("reseller.deviceStock.noSimCard"); })()}
                            </span>
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                          <Command>
                            <CommandInput placeholder={t("reseller.deviceStock.searchSimCard") || "Search SIM..."} />
                            <CommandList>
                              <CommandEmpty>{t("reseller.deviceStock.noSimCardFound") || "No SIM found."}</CommandEmpty>
                              <CommandGroup>
                                <CommandItem value="none" onSelect={() => { setSelectedSimId("none"); setSimOpen(false); }}>
                                  <Check className={cn("mr-2 h-4 w-4", selectedSimId === "none" ? "opacity-100" : "opacity-0")} />
                                  {t("reseller.deviceStock.noSimCard")}
                                </CommandItem>
                                <CommandItem value="__create_new__" onSelect={() => { setCreateNewSim(true); setSelectedSimId("none"); setSimOpen(false); }}>
                                  <Plus className="mr-2 h-4 w-4" />
                                  {t("reseller.deviceStock.createNewSim") || "Create new SIM"}
                                </CommandItem>
                                {[...simCards].sort((a, b) => a.iccid.localeCompare(b.iccid)).map((s) => {
                                  const label = `${s.iccid}${s.phone_number ? ` (${s.phone_number})` : ""}`;
                                  return (
                                    <CommandItem key={s.id} value={label} onSelect={() => { setSelectedSimId(s.id); setSimOpen(false); }}>
                                      <Check className={cn("mr-2 h-4 w-4", selectedSimId === s.id ? "opacity-100" : "opacity-0")} />
                                      {label}
                                    </CommandItem>
                                  );
                                })}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="supplier">{t("reseller.deviceStock.supplier")}</Label>
                    <Input id="supplier" name="supplier" defaultValue={editingDevice?.supplier || ""} />
                  </div>
                </div>
                {createNewSim && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="new_sim_iccid">ICCID *</Label>
                      <Input id="new_sim_iccid" value={newSimIccid} onChange={(e) => setNewSimIccid(e.target.value)} required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="new_sim_phone">{t("reseller.simCard.phoneNumber") || "Phone Number"}</Label>
                      <Input id="new_sim_phone" value={newSimPhone} onChange={(e) => setNewSimPhone(e.target.value)} />
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>{t("reseller.deviceStock.location")}</Label>
                      {stockLocations.length > 0 && (
                        <button type="button" className="text-xs text-muted-foreground hover:text-foreground underline" onClick={() => setIsManageLocationsOpen(true)}>
                          {t("reseller.deviceStock.manageLocationsLink")}
                        </button>
                      )}
                    </div>
                    {createNewLocation ? (
                      <Button type="button" variant="ghost" className="w-full justify-start font-normal text-muted-foreground" onClick={() => { setCreateNewLocation(false); setNewLocationName(""); setSelectedLocationId("none"); }}>
                        ← {t("common.cancel") || "Cancel"}
                      </Button>
                    ) : (
                      <Popover open={locationOpen} onOpenChange={setLocationOpen}>
                        <PopoverTrigger asChild>
                          <Button variant="outline" role="combobox" aria-expanded={locationOpen} className="w-full justify-between font-normal">
                            <span className="truncate">
                              {selectedLocationId === "none"
                                ? (t("reseller.deviceStock.noLocation") || "No location")
                                : (stockLocations.find((l) => l.id === selectedLocationId)?.name || (t("reseller.deviceStock.noLocation") || "No location"))}
                            </span>
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                          <Command>
                            <CommandInput placeholder={t("reseller.deviceStock.searchLocation") || "Search location..."} />
                            <CommandList>
                              <CommandEmpty>{t("reseller.deviceStock.noLocationFound") || "No location found."}</CommandEmpty>
                              <CommandGroup>
                                <CommandItem value="none" onSelect={() => { setSelectedLocationId("none"); setLocationOpen(false); }}>
                                  <Check className={cn("mr-2 h-4 w-4", selectedLocationId === "none" ? "opacity-100" : "opacity-0")} />
                                  {t("reseller.deviceStock.noLocation") || "No location"}
                                </CommandItem>
                                <CommandItem value="__create_new_location__" onSelect={() => { setCreateNewLocation(true); setSelectedLocationId("none"); setLocationOpen(false); }}>
                                  <Plus className="mr-2 h-4 w-4" />
                                  {t("reseller.deviceStock.createNewLocation") || "Create new location"}
                                </CommandItem>
                                {[...stockLocations].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })).map((l) => (
                                  <CommandItem key={l.id} value={l.name} onSelect={() => { setSelectedLocationId(l.id); setLocationOpen(false); }}>
                                    <Check className={cn("mr-2 h-4 w-4", selectedLocationId === l.id ? "opacity-100" : "opacity-0")} />
                                    {l.name}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    )}
                  </div>
                  {createNewLocation && (
                    <div className="space-y-2">
                      <Label htmlFor="new_location_name">{t("reseller.deviceStock.locationName") || "Location name"} *</Label>
                      <div className="flex gap-2">
                        <Input id="new_location_name" value={newLocationName} onChange={(e) => setNewLocationName(e.target.value)} className="flex-1" />
                        <Button type="button" onClick={handleSaveNewLocation} disabled={savingLocation || !newLocationName.trim()}>
                          {savingLocation ? <Loader2 className="h-4 w-4 animate-spin" /> : (t("common.save") || "Save")}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="status">{t("reseller.deviceStock.status")}</Label>
                  <Select name="status" defaultValue={editingDevice?.status || "in_stock"}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="in_stock">{t("reseller.deviceStock.statuses.in_stock")}</SelectItem>
                      <SelectItem value="deployed">{t("reseller.deviceStock.statuses.deployed")}</SelectItem>
                      <SelectItem value="returned">{t("reseller.deviceStock.statuses.returned")}</SelectItem>
                      <SelectItem value="maintenance">{t("reseller.deviceStock.statuses.maintenance")}</SelectItem>
                      <SelectItem value="retired">{t("reseller.deviceStock.statuses.retired")}</SelectItem>
                      <SelectItem value="deleted">{t("reseller.deviceStock.statuses.deleted")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="purchase_date">{t("reseller.deviceStock.purchaseDate")}</Label>
                    <Input id="purchase_date" name="purchase_date" type="date" defaultValue={editingDevice?.purchase_date || ""} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="purchase_price">{t("reseller.deviceStock.purchasePrice")}</Label>
                    <Input id="purchase_price" name="purchase_price" type="number" step="0.01" defaultValue={editingDevice?.purchase_price || ""} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="warranty_expiry">{t("reseller.deviceStock.warrantyExpiry")}</Label>
                    <Input id="warranty_expiry" name="warranty_expiry" type="date" defaultValue={editingDevice?.warranty_expiry || ""} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes">{t("reseller.deviceStock.notes")}</Label>
                  <Textarea id="notes" name="notes" defaultValue={editingDevice?.notes || ""} />
                </div>
                <Button type="submit" className="w-full" disabled={createMutation.isPending || updateMutation.isPending}>
                  {editingDevice ? t("common.update") : t("common.create")}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardHeader>
            <div className="flex flex-wrap gap-4">
              <div className="relative flex-1 min-w-[200px] max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t("reseller.deviceStock.searchPlaceholder")}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }} >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder={t("reseller.deviceStock.filterByStatus")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("common.all")}</SelectItem>
                  <SelectItem value="in_stock">{t("reseller.deviceStock.statuses.in_stock")}</SelectItem>
                  <SelectItem value="deployed">{t("reseller.deviceStock.statuses.deployed")}</SelectItem>
                  <SelectItem value="returned">{t("reseller.deviceStock.statuses.returned")}</SelectItem>
                  <SelectItem value="maintenance">{t("reseller.deviceStock.statuses.maintenance")}</SelectItem>
                  <SelectItem value="retired">{t("reseller.deviceStock.statuses.retired")}</SelectItem>
                  <SelectItem value="deleted">{t("reseller.deviceStock.statuses.deleted")}</SelectItem>
                </SelectContent>
              </Select>
              {(selectedIds.size > 0 || bulkDeleting) && (
                <div className="flex items-center gap-3">
                  <Button
                    variant="destructive"
                    onClick={handleBulkDelete}
                    disabled={bulkDeleting}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {bulkDeleteProgress
                      ? `${bulkDeleteProgress.done} / ${bulkDeleteProgress.total}`
                      : `${t("common.delete")} (${selectedIds.size})`}
                  </Button>
                  {bulkDeleteProgress && (
                    <div className="flex items-center gap-2 min-w-[200px]">
                      <div className="flex-1 bg-muted rounded-full h-2">
                        <div
                          className="bg-destructive h-2 rounded-full transition-all"
                          style={{ width: `${(bulkDeleteProgress.done / bulkDeleteProgress.total) * 100}%` }}
                        />
                      </div>
                      <span className="text-sm text-muted-foreground whitespace-nowrap">
                        {Math.round((bulkDeleteProgress.done / bulkDeleteProgress.total) * 100)}%
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">{t("common.loading")}</div>
            ) : total === 0 ? (
              <div className="text-center py-8 text-muted-foreground">{t("reseller.deviceStock.noDevices")}</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40px]">
                      <Checkbox
                        checked={devices.length > 0 && selectedIds.size === devices.length}
                        onCheckedChange={toggleSelectAll}
                      />
                    </TableHead>
                    {([
                      ["imei", "ID"],
                      ["serial_number", t("reseller.deviceStock.serialNumber")],
                      ["model", t("reseller.deviceStock.model")],
                      ["status", t("reseller.deviceStock.status")],
                    ] as const).map(([col, label]) => (
                      <TableHead key={col}>
                        <button className="flex items-center gap-1 hover:text-foreground" onClick={() => {
                          if (sortBy === col) setSortDir((d) => d === "asc" ? "desc" : "asc");
                          else { setSortBy(col); setSortDir("asc"); }
                          setPage(1);
                        }}>
                          {label}
                          {sortBy === col ? (sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
                        </button>
                      </TableHead>
                    ))}
                    <TableHead>{t("reseller.deviceStock.location")}</TableHead>
                    <TableHead>{t("reseller.deviceStock.customer")}</TableHead>
                    <TableHead>{t("reseller.deviceStock.vehicle")}</TableHead>
                    <TableHead>{t("reseller.deviceStock.simCard")}</TableHead>
                    <TableHead className="w-[150px]">{t("reseller.common.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {devices.map((device) => (
                    <TableRow key={device.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedIds.has(device.id)}
                          onCheckedChange={() => toggleSelect(device.id)}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{device.imei}</TableCell>
                      <TableCell>{device.serial_number || "-"}</TableCell>
                      <TableCell>{[device.manufacturer, device.model].filter(Boolean).join(" ") || device.model}</TableCell>
                      <TableCell>
                        <Badge className={statusColors[device.status]}>{t(`reseller.deviceStock.statuses.${device.status}`)}</Badge>
                      </TableCell>
                      <TableCell>{device.location?.name || "-"}</TableCell>
                      <TableCell>
                        {(() => {
                          // Resolve through IMEI first, just like the vehicle column;
                          // legacy traccar_device_id values may be stale.
                          const traccarDeviceId = traccarDeviceByUniqueId.get(normalizeImei(device.imei))?.id
                            ?? device.traccar_device_id;
                          const customer = traccarDeviceId
                            ? customerByTraccarDeviceId.get(traccarDeviceId)
                            : undefined;
                          return customer ? (
                            <button
                              className="hover:underline text-sm text-left"
                              onClick={() => navigate(`/devices?search=${encodeURIComponent(customer.name)}`)}
                            >
                              {customer.name}
                            </button>
                          ) : "-";
                        })()}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const matched = device.imei ? traccarDeviceByUniqueId.get(normalizeImei(device.imei)) : undefined;
                          return matched ? (
                            <button
                              className="hover:underline text-sm text-left"
                              onClick={() => navigate(`/devices?search=${encodeURIComponent(matched.uniqueId)}`)}
                            >
                              {matched.name}
                            </button>
                          ) : "-";
                        })()}
                      </TableCell>
                      <TableCell>
                        {device.sim_card ? (
                          <button className="hover:underline text-sm text-left" onClick={() => navigate(`/sim-cards?search=${encodeURIComponent(device.sim_card!.iccid)}`)}>
                            {device.sim_card.phone_number || device.sim_card.iccid}
                          </button>
                        ) : "-"}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="icon" onClick={() => { setEditingDevice(device); setSelectedManufacturer(device.manufacturer || ""); setSelectedModel(device.model || ""); setSelectedSimId(device.sim_card_id || "none"); setSelectedLocationId(device.location_id || "none"); setIsOpen(true); }}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>{t("common.edit")}</TooltipContent>
                          </Tooltip>
                          {(device.status === "in_stock" || device.status === "returned") && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button variant="ghost" size="icon" onClick={() => { setSelectedDevice(device); setIsDeployOpen(true); }}>
                                  <Rocket className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>{t("reseller.deviceStock.deploy")}</TooltipContent>
                            </Tooltip>
                          )}
                          {device.status === "deployed" && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button variant="ghost" size="icon" onClick={() => { setSelectedDevice(device); setIsReturnOpen(true); }}>
                                  <Undo2 className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>{t("reseller.deviceStock.return")}</TooltipContent>
                            </Tooltip>
                          )}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="icon" onClick={() => { setHistoryDevice(device); setIsHistoryOpen(true); }}>
                                <History className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>{t("reseller.deviceStock.history")}</TooltipContent>
                          </Tooltip>
                          {device.status !== "deleted" && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button variant="ghost" size="icon" onClick={() => { if (confirm(t("reseller.common.deleteConfirm"))) updateMutation.mutate({ id: device.id, data: { status: "deleted" } }); }}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>{t("common.delete")}</TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-1 pt-4">
                <span className="text-sm text-muted-foreground">
                  {t("common.showingRange", { from: (page - 1) * PAGE_SIZE + 1, to: Math.min(page * PAGE_SIZE, total), total })}
                </span>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPage((p) => p - 1)} disabled={page === 1}>{t("common.previous")}</Button>
                  <span className="text-sm text-muted-foreground">{t("common.pageOf", { page, total: totalPages })}</span>
                  <Button variant="outline" size="sm" onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages}>{t("common.next")}</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Deploy Dialog */}
        <Dialog open={isDeployOpen} onOpenChange={(open) => { setIsDeployOpen(open); if (!open) resetDeployState(); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("reseller.deviceStock.deployDevice")}</DialogTitle>
              <DialogDescription className="sr-only">
                Select a customer and vehicle to install this stock device.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleDeploySubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>{t("reseller.deviceStock.customer")} *</Label>
                <Popover open={deployCustomerOpen} onOpenChange={setDeployCustomerOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" aria-expanded={deployCustomerOpen} className="w-full justify-between font-normal">
                      {deployCustomerId ? customers.find((c) => c.id === deployCustomerId)?.name : t("reseller.deviceStock.selectCustomer")}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-[--radix-popover-trigger-width] p-0"
                    align="start"
                    style={{ maxHeight: 'min(15rem, var(--radix-popover-content-available-height))' }}
                  >
                    <Command className="max-h-full">
                      <CommandInput placeholder={t("reseller.deviceStock.selectCustomer")} />
                      <CommandList className="max-h-none flex-1 overflow-y-auto">
                        <CommandEmpty>{t("common.noResults")}</CommandEmpty>
                        <CommandGroup>
                          <CommandItem value="__create_new_customer__" className="text-primary font-medium" onSelect={() => { setIsAddCustomerFromDeploy(true); setDeployCustomerOpen(false); }}>
                            + {t("reseller.customer.add")}
                          </CommandItem>
                          {[...customers].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })).map((c) => (
                            <CommandItem key={c.id} value={c.name} onSelect={() => { setDeployCustomerId(c.id); setDeployTraccarDeviceId(""); setDeployCustomerOpen(false); }}>
                              <Check className={cn("mr-2 h-4 w-4", deployCustomerId === c.id ? "opacity-100" : "opacity-0")} />
                              {c.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>{t("reseller.deviceStock.vehicle")} *</Label>
                <Popover open={deployVehicleOpen} onOpenChange={(open) => { setDeployVehicleOpen(open); if (!open) setDeployVehicleSearch(""); }}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" aria-expanded={deployVehicleOpen} className="w-full justify-between font-normal" disabled={!deployCustomerId}>
                      {deployTraccarDeviceId ? customerVehicles.find((d) => String(d.id) === deployTraccarDeviceId)?.name : (deployCustomerId ? t("reseller.deviceStock.selectVehicle") : t("reseller.deviceStock.selectCustomerFirst"))}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput placeholder={t("reseller.deviceStock.selectVehicle")} value={deployVehicleSearch} onValueChange={setDeployVehicleSearch} />
                      <CommandList>
                        <CommandEmpty>{t("common.noResults")}</CommandEmpty>
                        <CommandGroup>
                          {[...customerVehicles].sort((a, b) => a.name.localeCompare(b.name)).map((d) => {
                            const hasExistingDevice = Boolean(d.uniqueId) && !d.uniqueId.startsWith("not_installed_");
                            return (
                              <CommandItem key={d.id} value={d.name} onSelect={() => { setDeployTraccarDeviceId(String(d.id)); setDeployVehicleSearch(""); setDeployVehicleOpen(false); }}>
                                <Check className={cn("mr-2 h-4 w-4", deployTraccarDeviceId === String(d.id) ? "opacity-100" : "opacity-0")} />
                                {d.name}
                                {hasExistingDevice && (
                                  <span className="ml-auto text-xs text-muted-foreground">{t("reseller.deviceStock.alreadyInstalled")}</span>
                                )}
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                    <div className="border-t p-1">
                      <button
                        type="button"
                        className="w-full text-left text-sm text-primary font-medium px-2 py-1.5 rounded hover:bg-accent"
                        onClick={() => { setIsAddVehicleFromDeploy(true); setDeployVehicleSearch(""); setDeployVehicleOpen(false); }}
                      >
                        + {t("reseller.vehicle.add")}
                      </button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <Button type="submit" className="w-full" disabled={isDeploying || !deployCustomerId || !deployTraccarDeviceId || !customerVehicles.some((d) => String(d.id) === deployTraccarDeviceId)}>
                {isDeploying && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {t("reseller.deviceStock.deploy")}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        <AlertDialog open={!!overwriteWarningVehicle} onOpenChange={(open) => { if (!open) setOverwriteWarningVehicle(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("reseller.deviceStock.overwriteDeviceTitle")}</AlertDialogTitle>
              <AlertDialogDescription>
                {t("reseller.deviceStock.overwriteDeviceDescription", {
                  vehicle: overwriteWarningVehicle?.name,
                  uniqueId: overwriteWarningVehicle?.uniqueId,
                })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (overwriteWarningVehicle && selectedDevice) {
                    performDeploy(overwriteWarningVehicle, selectedDevice, deployCustomerId);
                  }
                  setOverwriteWarningVehicle(null);
                }}
              >
                {t("reseller.deviceStock.overwriteDeviceConfirm")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AddVehicleDialog
          open={isAddVehicleFromDeploy}
          onOpenChange={(open) => { if (!open) setIsAddVehicleFromDeploy(false); }}
          linkToUserId={selectedCustomer?.traccar_user_id ?? undefined}
          onCreated={(device) => {
            setDeployTraccarDeviceId(String(device.id));
          }}
        />

        <CustomerDialog
          open={isAddCustomerFromDeploy}
          onOpenChange={(open) => { if (!open) setIsAddCustomerFromDeploy(false); }}
          defaultTraccarMode="new"
          onSaved={(customer) => {
            setDeployCustomerId(customer.id);
            setDeployTraccarDeviceId("");
          }}
        />

        {/* Return Dialog */}
        <Dialog open={isReturnOpen} onOpenChange={(open) => { setIsReturnOpen(open); if (!open) setSelectedDevice(null); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("reseller.deviceStock.returnDevice")}</DialogTitle>
              <DialogDescription className="sr-only">
                Return this installed device to stock and record an optional reason.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleReturnSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>{t("reseller.deviceStock.serialNumber")}</Label>
                <Input value={selectedDevice?.serial_number || ""} disabled />
              </div>
              <div className="space-y-2">
                <Label>{t("reseller.deviceStock.currentCustomer")}</Label>
                <Input value={selectedDevice?.customer?.name || "-"} disabled />
              </div>
              <div className="space-y-2">
                <Label htmlFor="return_reason">{t("reseller.deviceStock.returnReason")}</Label>
                <Textarea id="return_reason" name="return_reason" placeholder={t("reseller.deviceStock.returnReasonPlaceholder")} />
              </div>
              <Button type="submit" className="w-full" disabled={isReturning || returnMutation.isPending}>
                {(isReturning || returnMutation.isPending) && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {t("reseller.deviceStock.return")}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Manage Locations Dialog */}
        <Dialog open={isManageLocationsOpen} onOpenChange={setIsManageLocationsOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>{t("reseller.deviceStock.manageLocations")}</DialogTitle>
              <DialogDescription className="sr-only">
                Review and remove stock locations.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-1 max-h-[400px] overflow-y-auto">
              {[...stockLocations].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })).map((loc) => (
                <div key={loc.id} className="flex items-center justify-between px-2 py-1.5 rounded hover:bg-muted">
                  <span className="text-sm">{loc.name}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={deleteLocationMutation.isPending}
                    onClick={() => {
                      if (confirm(t("reseller.deviceStock.deleteLocationConfirm", { name: loc.name }))) {
                        deleteLocationMutation.mutate(loc.id);
                      }
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              {stockLocations.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">{t("reseller.deviceStock.noLocations")}</p>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* History Dialog */}
        <Dialog open={isHistoryOpen} onOpenChange={(open) => { setIsHistoryOpen(open); if (!open) setHistoryDevice(null); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{t("reseller.deviceStock.history")} - {historyDevice?.serial_number}</DialogTitle>
              <DialogDescription className="sr-only">
                Review deployment and return history for this stock device.
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-[400px] overflow-y-auto">
              {historyLoading ? (
                <div className="text-center py-4 text-muted-foreground">{t("common.loading")}</div>
              ) : history.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">{t("reseller.deviceStock.noHistory")}</div>
              ) : (
                <div className="space-y-4">
                  {history.map((entry) => (
                    <div key={entry.id} className="border-l-2 border-muted pl-4 pb-4">
                      <div className="flex items-center gap-2">
                        <Badge variant={entry.action === "deployed" ? "default" : "secondary"}>
                          {t(`reseller.deviceStock.actions.${entry.action}`)}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {new Date(entry.created_at).toLocaleString()}
                        </span>
                      </div>
                      {entry.customer && (
                        <p className="text-sm mt-1">{t("reseller.deviceStock.customer")}: {entry.customer.name}</p>
                      )}
                      {entry.notes && (
                        <p className="text-sm text-muted-foreground mt-1">{entry.notes}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
